import { Injectable } from '@angular/core';
import { Query } from '@datorama/akita';
import { MaterialDict, MaterialDictEntry, MaterialState, MaterialStore } from './material-store';
import { CustomEntityForSave, CustomMaterial } from '@shared/models/custom-material';
import {
	ProcedureSpecificationMaterialRule,
	SpecificationRule
} from '@core/procedure-helpers/models/specification-material-rules-config';
import {
	decodeCustomMaterialEntityId,
	getOriginalRxMaterialFromTeeth,
	getOriginalRxSpecificationFromTeeth
} from '@shared/utils/material-util';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { MaterialIdType } from '@core/procedure-helpers/models/material.enum';
import { SpecificationIdType } from '@core/procedure-helpers/models/specification.enum';
import { CustomSpecification } from '@shared/models/custom-specification';

@Injectable({ providedIn: 'root' })
export class MaterialQuery extends Query<MaterialState> {
	constructor(
		store: MaterialStore,
		private shellQuery: ShellQuery
	) {
		super(store);
	}

	/**
	 * Get a material without checking the revision.
	 * This is a workaround to find material groups for materials that where already deleted or removed.
	 * We only get the latest revisions from all-entities call, so we can't tind the exact material.
	 * Howerver we know that in the currect design neither a material group, nor an iTeroMaterialId or iTeroSpecificationId
	 * of a material can be changed throughout the revisions.
	 * @param materialId - The material ID to get.
	 * @returns The material or undefined if not found.
	 */
	getMaterialWithoutRevisionCheck(materialId: string): CustomMaterial | undefined {
		const decodedId = decodeCustomMaterialEntityId(materialId);

		if (!decodedId.id || !decodedId.labId) {
			return undefined;
		}

		const materialKeys = Object.keys(this.store.getValue().materials);
		const key = materialKeys.find(k => {
			const decodedKeyId = decodeCustomMaterialEntityId(k);

			return decodedKeyId.id === decodedId.id && decodedKeyId.labId === decodedId.labId;
		});

		return key ? this.store.getValue().materials[key] : undefined;
	}

	/**
	 * Get a specification without checking the revision.
	 * This is a workaround to find specification groups for specifications that where already deleted or removed.
	 * We only get the latest revisions from all-entities call, so we can't tind the exact specification.
	 * Howerver we know that in the currect design an iTeroSpecificationId of a specification cannot be changed throughout the revisions.
	 * @param specificationId - The specification ID to get.
	 * @returns The specification or undefined if not found.
	 */
	getSpecificationWithoutRevisionCheck(specificationId: string): CustomSpecification | undefined {
		const decodedId = decodeCustomMaterialEntityId(specificationId);

		if (!decodedId.id || !decodedId.labId) {
			return undefined;
		}

		const specificationKeys = Object.keys(this.store.getValue().specs);
		const key = specificationKeys.find(k => {
			const decodedKeyId = decodeCustomMaterialEntityId(k);

			return decodedKeyId.id === decodedId.id && decodedKeyId.labId === decodedId.labId;
		});

		return key ? this.store.getValue().specs[key] : undefined;
	}

	getMaterialForSave(materialId: number | string): CustomEntityForSave {
		const customMaterial = this.store.getValue().materials[materialId];

		if (customMaterial) {
			return customMaterial;
		}

		// @ts-expect-error - TODO: fix this type error
		return getOriginalRxMaterialFromTeeth(materialId, this.shellQuery.rx?.Teeth ?? []);
	}

	getSpecificationForSave(specificationId: number | string): CustomEntityForSave {
		const customSpecification = this.store.getValue().specs[specificationId];

		if (customSpecification) {
			return customSpecification;
		}

		// @ts-expect-error - TODO: fix this type error
		return getOriginalRxSpecificationFromTeeth(specificationId, this.shellQuery.rx?.Teeth ?? []);
	}

	getAsMergedSpecificationMaterialRule(
		labId: number,
		origRule: ProcedureSpecificationMaterialRule,
		isBridge: boolean
	): ProcedureSpecificationMaterialRule {
		const labCustoms = isBridge ? this.store.getValue().bridgeDictionary[labId] : this.store.getValue().nonBridgeDictionary[labId];

		if (!labCustoms) {
			return origRule;
		}

		return {
			...origRule,
			UnitTypeRules: origRule.UnitTypeRules.map(unitTypeRule => {
				const dict = labCustoms[unitTypeRule.Id as number];

				if (!unitTypeRule.Specifications?.length && (!dict || Object.keys(dict).length === 0)) {
					return unitTypeRule;
				}

				const specifications = this.getIteroSpecIdsAndCustomSpecs(unitTypeRule.Specifications ?? [], dict);

				return {
					...unitTypeRule,
					Specifications: specifications
				};
			})
		};
	}

	private getIteroSpecIdsAndCustomSpecs(iteroSpecIdsWithMaterialIds: SpecificationRule[], dict: MaterialDict): SpecificationRule[] {
		if (!dict) {
			return iteroSpecIdsWithMaterialIds;
		}

		const fullSpecs = this.store.getValue().specs;

		// Merge iteroSpecIdsWithMaterialIds and dict preserving their original order, merge materials using this.mergeMaterials()
		const handledITeroSpecs = new Map<SpecificationIdType, MaterialIdType[]>();
		const mergedSpecRules: SpecificationRule[] = [];

		// Add all iteroSpecIdsWithMaterialIds, merged with dict if present
		for (const rule of iteroSpecIdsWithMaterialIds) {
			const dictEntry = dict[rule.Id];
			let mergedMaterials: MaterialIdType[];

			if (dictEntry) {
				mergedMaterials = this.mergeMaterials(rule.Materials, dictEntry);
			} else {
				mergedMaterials = rule.Materials;
			}
			mergedSpecRules.push({
				Id: rule.Id,
				Materials: mergedMaterials
			});
			handledITeroSpecs.set(rule.Id, mergedMaterials);
		}

		const removedSpecIds = new Set<string | number>();

		// Now add entries from dict that are not present in iteroSpecIdsWithMaterialIds
		for (const [specId, materialDict] of Object.entries(dict)
			.filter(([customSpecId]) => {
				const decodedKeyId = decodeCustomMaterialEntityId(customSpecId);

				return decodedKeyId.id !== undefined && decodedKeyId.labId !== undefined;
			})
			.sort((a, b) => (fullSpecs[a[0]]?.orderPosition ?? 0) - (fullSpecs[b[0]]?.orderPosition ?? 0))) {
			const correspondingIteroSpecId = fullSpecs[specId]?.iTeroSpecificationId;

			let correspondingIteroMaterials: MaterialIdType[] = [];

			if (correspondingIteroSpecId) {
				removedSpecIds.add(correspondingIteroSpecId);
				correspondingIteroMaterials = handledITeroSpecs.get(correspondingIteroSpecId) ?? [];
			}

			mergedSpecRules.push({
				Id: specId,
				Materials: this.mergeMaterials(correspondingIteroMaterials, materialDict)
			});
		}

		return mergedSpecRules.filter(
			spec => !removedSpecIds.has(spec.Id) && fullSpecs[spec.Id]?.isAvailable !== false && !!spec.Materials?.length
		);
	}

	private mergeMaterials(iteroMaterials: MaterialIdType[], customMaterials: MaterialDictEntry): MaterialIdType[] {
		const fullMaterials = this.store.getValue().materials;

		if (!customMaterials?.customMaterialIds?.length && !Object.keys(customMaterials?.customMaterialIdsByITeroMaterialId ?? {}).length) {
			return iteroMaterials;
		}

		if (!iteroMaterials?.length) {
			return (customMaterials.customMaterialIds ?? [])
				.filter(id => fullMaterials[id]?.isAvailable !== false)
				.sort((a, b) => (fullMaterials[a]?.orderPosition ?? 0) - (fullMaterials[b]?.orderPosition ?? 0));
		}

		// Always merge the provided iteroMaterials with any customMaterialIds in customMaterials
		const { customMaterialIds = [], customMaterialIdsByITeroMaterialId = {} } = customMaterials;
		const seen = new Set<MaterialIdType>();
		const merged: MaterialIdType[] = [];

		// Add iteroMaterials (substituting with custom if available), preserving order
		for (const material of iteroMaterials) {
			const value = customMaterialIdsByITeroMaterialId[material as number] ?? material;

			if (!seen.has(value)) {
				merged.push(value);
				seen.add(value);
			}
		}
		// Add customMaterials not already added, preserving their order
		for (const id of customMaterialIds.sort(
			(a, b) => (fullMaterials[a]?.orderPosition ?? 0) - (fullMaterials[b]?.orderPosition ?? 0)
		)) {
			if (!seen.has(id)) {
				merged.push(id);
				seen.add(id);
			}
		}

		return merged.filter(id => fullMaterials[id]?.isAvailable !== false);
	}
}
